import {
  Paper,
  Text,
  Group,
  Badge,
  Stack,
  Accordion,
  List,
  Divider,
  Anchor,
  Tooltip,
} from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import { Link } from "wouter";
import RenderedText from "../_Common/RenderedText";

const CONFIDENCE_COLOR = { high: "green", medium: "yellow", low: "red" };

function creditsToUsd(credits) {
  return `$${((credits ?? 0) / 1_000_000).toFixed(3)}`;
}

/**
 * Dissent-first rendering of one deliberation: the single synthesized answer,
 * then an explicit consensus/dissent banner (the value of a panel), then the
 * expandable per-seat transcript.
 */
export default function PanelTurnView({ turn, displayMode = "markdown" }) {
  const dissents = turn.dissents ?? [];
  const agreements = turn.agreements ?? [];
  const contributions = turn.contributions ?? [];
  const confidence = (turn.confidence || "").toLowerCase();
  const roundsExecuted = turn.roundsExecuted ?? 1;
  const isDebate = roundsExecuted > 1;

  return (
    <Paper withBorder radius="md" p="md" mb="sm">
      {/* Primary: the one answer */}
      <RenderedText text={turn.synthesizedAnswer} mode={displayMode} />

      {/* Consensus / dissent banner */}
      {(agreements.length > 0 || dissents.length > 0 || confidence || isDebate) && (
        <>
          <Divider my="sm" />
          <Group gap="xs" mb="xs">
            <Text size="sm" fw={600}>
              Panel consensus
            </Text>
            {confidence && (
              <Badge
                size="sm"
                variant="light"
                color={CONFIDENCE_COLOR[confidence] ?? "gray"}
              >
                {confidence} confidence
              </Badge>
            )}
            {isDebate && (
              <Badge size="sm" variant="light" color="indigo">
                debate · {roundsExecuted} round{roundsExecuted > 1 ? "s" : ""}
              </Badge>
            )}
            {turn.convergedEarly && (
              <Badge size="sm" variant="light" color="teal">
                converged early
              </Badge>
            )}
            {turn.chairTruncated && (
              <Tooltip
                label="The chair hit its output limit mid-synthesis, so this answer is partial and the agreements/dissents below may be missing"
                withArrow
                multiline
                w={280}
              >
                <Badge size="sm" variant="light" color="orange">
                  truncated
                </Badge>
              </Tooltip>
            )}
          </Group>

          {agreements.length > 0 && (
            <List size="sm" spacing={2} mb={dissents.length ? "sm" : 0}>
              {agreements.map((a, i) => (
                <List.Item key={i}>
                  <RenderedText text={a} mode={displayMode} size="sm" />
                </List.Item>
              ))}
            </List>
          )}

          {dissents.length > 0 && (
            <Stack gap="xs">
              {dissents.map((d, i) => (
                <Paper key={i} withBorder radius="sm" p="xs" bg="var(--mantine-color-yellow-light)">
                  <Group gap="xs" mb={2}>
                    <Badge size="xs" color="orange" variant="filled">
                      Dissent
                    </Badge>
                    <Text size="sm" fw={600}>
                      {d.seat}
                    </Text>
                  </Group>
                  <RenderedText text={d.position} mode={displayMode} size="sm" />
                  {d.reasoning && (
                    <RenderedText
                      text={d.reasoning}
                      mode={displayMode}
                      size="xs"
                      c="dimmed"
                      style={{ marginTop: 2 }}
                    />
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </>
      )}

      {/* Expandable per-seat transcript, grouped by round for debates */}
      {contributions.length > 0 && (
        <Accordion variant="separated" mt="sm">
          <Accordion.Item value="panelists">
            <Accordion.Control>
              <Text size="sm">
                {isDebate ? "Debate transcript" : "Panelist responses"} (
                {contributions.length}) · {creditsToUsd(turn.totalCostCredits)}
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              {isDebate ? (
                <Stack gap="md">
                  {groupByRound(contributions).map(({ round, items }) => (
                    <div key={round}>
                      <Divider
                        label={`Round ${round}${round === 1 ? " (blind)" : ""}`}
                        labelPosition="left"
                        mb="xs"
                      />
                      <Stack>
                        {items.map((c, i) => (
                          <ContributionItem
                            key={i}
                            c={c}
                            divider={i < items.length - 1}
                            displayMode={displayMode}
                          />
                        ))}
                      </Stack>
                    </div>
                  ))}
                </Stack>
              ) : (
                <Stack>
                  {contributions.map((c, i) => (
                    <ContributionItem
                      key={i}
                      c={c}
                      divider={i < contributions.length - 1}
                      displayMode={displayMode}
                    />
                  ))}
                </Stack>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
    </Paper>
  );
}

/** Groups contributions by their round number, ascending. */
function groupByRound(contributions) {
  const byRound = new Map();
  for (const c of contributions) {
    const r = c.round ?? 1;
    if (!byRound.has(r)) byRound.set(r, []);
    byRound.get(r).push(c);
  }
  return [...byRound.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([round, items]) => ({ round, items }));
}

/** One seat's contribution: persona + model badge + optional run link + its text. */
function ContributionItem({ c, divider, displayMode }) {
  return (
    <div>
      <Group gap="xs" mb={2}>
        <Text size="sm" fw={600}>
          {c.seatName}
        </Text>
        <Badge size="xs" variant="light" color="gray">
          {c.model}
        </Badge>
        {c.truncated && (
          <Tooltip label="This panelist hit its output limit — the answer is cut off" withArrow>
            <Badge size="xs" variant="light" color="orange">
              truncated
            </Badge>
          </Tooltip>
        )}
        {c.requestId && (
          <Anchor component={Link} to={`/logs/${c.requestId}`} size="xs">
            <Group gap={2}>
              view run <IconExternalLink size={11} />
            </Group>
          </Anchor>
        )}
      </Group>
      <RenderedText text={c.text} mode={displayMode} size="sm" c="dimmed" />
      {divider && <Divider my="xs" />}
    </div>
  );
}
